import React, { useState, useEffect, useRef } from 'react';

export default function ColorPickerPopover({ value, defaultColor, onApply, children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('hex');
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(100);
  const [light, setLight] = useState(50);
  const [hex, setHex] = useState(value || defaultColor || '#000000');
  const [rgb, setRgb] = useState({ r: 0, g: 0, b: 0 });
  const current = value || defaultColor || '#000000';
  const ref = useRef(null);
  const padRef = useRef(null);

  const hexToRgb = (h) => {
    h = h.replace('#', '');
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    if (h.length !== 6) return { r: 0, g: 0, b: 0 };
    return { r: parseInt(h.substring(0,2),16), g: parseInt(h.substring(2,4),16), b: parseInt(h.substring(4,6),16) };
  };

  const rgbToHex = (r, g, b) => '#' + [r,g,b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2,'0')).join('');

  const hexToHsl = (h) => {
    const { r, g, b } = hexToRgb(h);
    const r1 = r/255, g1 = g/255, b1 = b/255;
    const max = Math.max(r1,g1,b1), min = Math.min(r1,g1,b1);
    let hu = 0, s = 0, l = (max+min)/2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d/(2-max-min) : d/(max+min);
      if (max === r1) hu = ((g1-b1)/d + (g1 < b1 ? 6 : 0))/6;
      else if (max === g1) hu = ((b1-r1)/d + 2)/6;
      else hu = ((r1-g1)/d + 4)/6;
    }
    return [Math.round(hu*360), Math.round(s*100), Math.round(l*100)];
  };

  const hslToHex = (h, s, l) => {
    s /= 100; l /= 100;
    const f = (n) => { const k = (n + h/30) % 12; const a = s * Math.min(l, 1-l); return Math.round(255 * (l - a * Math.max(-1, Math.min(k-3, 9-k, 1)))); };
    return '#' + [f(0),f(8),f(4)].map(x => x.toString(16).padStart(2,'0')).join('');
  };

  const syncAll = (hexVal) => {
    setHex(hexVal);
    setRgb(hexToRgb(hexVal));
    const [h, s, l] = hexToHsl(hexVal);
    setHue(h); setSat(s); setLight(l);
  };

  const openPicker = () => { syncAll(current); setOpen(true); };

  const handlePadClick = (e) => {
    const rect = padRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const newS = Math.round(x * 100), newL = Math.round(100 - y * 100);
    setSat(newS); setLight(newL);
    const h = hslToHex(hue, newS, newL);
    setHex(h); setRgb(hexToRgb(h));
  };

  const handleHueChange = (h) => {
    setHue(h);
    const hx = hslToHex(h, sat, light);
    setHex(hx); setRgb(hexToRgb(hx));
  };

  const handleHexInput = (v) => {
    setHex(v);
    const clean = v.trim().replace('#','');
    if (/^[0-9a-f]{6}$/i.test(clean)) {
      const full = '#' + clean;
      setRgb(hexToRgb(full));
      const [h, s, l] = hexToHsl(full);
      setHue(h); setSat(s); setLight(l);
    }
  };

  const handleRgbInput = (channel, val) => {
    const n = val === '' ? 0 : Math.max(0, Math.min(255, parseInt(val) || 0));
    const newRgb = { ...rgb, [channel]: n };
    setRgb(newRgb);
    const hx = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(hx);
    const [h, s, l] = hexToHsl(hx);
    setHue(h); setSat(s); setLight(l);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const tabStyle = (active) => ({
    flex: 1, padding: '4px 0', borderRadius: '4px', border: 'none', fontSize: '11px', fontWeight: '600',
    cursor: 'pointer', background: active ? '#2D87F0' : 'transparent', color: active ? '#fff' : '#64748B',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div onClick={openPicker} style={{ cursor: 'pointer' }}>
        {children}
      </div>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '6px', zIndex: 50, background: '#fff', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)', padding: '12px', width: '240px' }}>
          <div ref={padRef} onClick={handlePadClick}
            style={{ width: '100%', height: '130px', borderRadius: '6px', cursor: 'crosshair', position: 'relative',
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #808080, hsl(${hue},100%,50%))` }}>
            <div style={{ position: 'absolute', left: `${sat}%`, top: `${100 - light}%`, width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.5)', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
          </div>
          <input type="range" min="0" max="360" value={hue} onChange={e => handleHueChange(Number(e.target.value))}
            style={{ width: '100%', marginTop: '10px', height: '14px', borderRadius: '7px', appearance: 'none', cursor: 'pointer',
              background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)' }} />
          <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '6px', padding: '2px', marginTop: '10px' }}>
            <button type="button" onClick={() => setMode('hex')} style={tabStyle(mode === 'hex')}>HEX</button>
            <button type="button" onClick={() => setMode('rgb')} style={tabStyle(mode === 'rgb')}>RGB</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #CBD5E1', backgroundColor: hex, flexShrink: 0 }} />
            {mode === 'hex' ? (
              <input className="input-field" type="text" value={hex} onChange={e => handleHexInput(e.target.value)}
                placeholder="#000000" style={{ flex: 1, fontFamily: 'monospace', fontSize: '13px', textAlign: 'center', padding: '4px 8px' }} />
            ) : (
              <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                {['r','g','b'].map(ch => (
                  <div key={ch} style={{ flex: 1, textAlign: 'center' }}>
                    <input className="input-field" type="number" min="0" max="255" value={rgb[ch]}
                      onChange={e => handleRgbInput(ch, e.target.value)}
                      style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', textAlign: 'center', padding: '4px 2px' }} />
                    <div style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '600', marginTop: '2px' }}>{ch.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
            <button type="button" onClick={() => setOpen(false)}
              style={{ flex: 1, padding: '7px 0', borderRadius: '6px', border: '1px solid #CBD5E1', background: '#fff', fontSize: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={() => { onApply(hex.startsWith('#') ? hex : '#' + hex); setOpen(false); }}
              style={{ flex: 1, padding: '7px 0', borderRadius: '6px', border: 'none', background: '#2D87F0', fontSize: '12px', fontWeight: '600', color: '#fff', cursor: 'pointer' }}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
